/**
 * Partner CRM v2 Dashboard tiles + employee analytics + CSV export.
 *
 * Drop-in component for /partner-portal or any admin view.
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Users, UserPlus, Activity, Clock, Calendar, CheckCircle2,
  XCircle, TrendingUp, Download, Loader2, BarChart3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TILES = [
  { key: 'total_leads', label: 'Total Leads', Icon: Users, tone: 'text-white' },
  { key: 'new_leads', label: 'New Leads', Icon: UserPlus, tone: 'text-blue-300' },
  { key: 'active_leads', label: 'Active', Icon: Activity, tone: 'text-purple-300' },
  { key: 'pending_followups', label: 'Pending Follow-ups', Icon: Clock, tone: 'text-orange-300' },
  { key: 'appointments', label: 'Appointments', Icon: Calendar, tone: 'text-cyan-300' },
  { key: 'conversions', label: 'Conversions', Icon: CheckCircle2, tone: 'text-emerald-300' },
  { key: 'lost_leads', label: 'Lost', Icon: XCircle, tone: 'text-red-300' },
  { key: 'conversion_rate_pct', label: 'Conv. Rate %', Icon: TrendingUp, tone: 'text-gold', suffix: '%' },
];

const PartnerCRMDashboardV2 = () => {
  const [data, setData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [d, e] = await Promise.all([
          axios.get(`${API}/crm/v2/dashboard`),
          axios.get(`${API}/crm/v2/employee-analytics`),
        ]);
        setData(d.data);
        setEmployees(e.data.employees || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/crm/v2/export.csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dreamlife-leads-${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="glass-card p-8 flex items-center gap-3 text-text-secondary">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading CRM dashboard…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="crm-v2-dashboard">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-gold" />
          <h2 className="font-playfair text-xl font-semibold">Performance Overview</h2>
          <span className="text-[10px] uppercase tracking-[0.2em] text-text-secondary ml-2">
            {data.scope === 'platform' ? 'Platform-wide' : 'Your assigned leads'}
          </span>
        </div>
        <Button
          onClick={handleExport}
          disabled={exporting}
          variant="outline"
          className="border-white/10 hover:bg-white/5"
          data-testid="crm-v2-export-csv-btn"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
          Export CSV
        </Button>
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {TILES.map((t, i) => {
          const value = data.tiles[t.key] ?? 0;
          return (
            <motion.div
              key={t.key}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="glass-card p-4 hover:border-gold/30 transition-colors"
              data-testid={`crm-tile-${t.key}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-[0.18em] text-text-secondary">{t.label}</span>
                <t.Icon className={`w-4 h-4 ${t.tone}`} />
              </div>
              <div className={`text-3xl font-semibold ${t.tone}`}>
                {value}{t.suffix || ''}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Stage funnel */}
      <div className="glass-card p-5">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Activity className="w-4 h-4 text-gold" /> Lead Funnel
        </h3>
        <div className="space-y-2">
          {data.by_stage.map((s) => {
            const pct = data.tiles.total_leads
              ? Math.round((s.count / data.tiles.total_leads) * 100)
              : 0;
            return (
              <div key={s.stage} className="flex items-center gap-3">
                <div className="w-44 text-xs text-text-secondary">{s.label}</div>
                <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-gold/60 to-gold rounded-full transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="w-12 text-right text-sm text-white">{s.count}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Employee analytics */}
      {employees.length > 0 && (
        <div className="glass-card p-5">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-gold" /> Employee Performance
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-secondary text-xs uppercase tracking-[0.12em]">
                  <th className="text-left py-2 font-medium">Employee</th>
                  <th className="text-right py-2 font-medium">Leads</th>
                  <th className="text-right py-2 font-medium">Active</th>
                  <th className="text-right py-2 font-medium">Won</th>
                  <th className="text-right py-2 font-medium">Lost</th>
                  <th className="text-right py-2 font-medium">Calls</th>
                  <th className="text-right py-2 font-medium">Conv %</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((e) => (
                  <tr key={e.employee_id} className="border-t border-white/5" data-testid={`employee-row-${e.employee_id}`}>
                    <td className="py-2 text-white">{e.employee_name}</td>
                    <td className="py-2 text-right text-white">{e.leads_assigned}</td>
                    <td className="py-2 text-right text-purple-300">{e.active}</td>
                    <td className="py-2 text-right text-emerald-300">{e.closed_won}</td>
                    <td className="py-2 text-right text-red-300">{e.closed_lost}</td>
                    <td className="py-2 text-right text-cyan-300">{e.calls}</td>
                    <td className="py-2 text-right text-gold font-semibold">{e.conversion_rate_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerCRMDashboardV2;
